'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { SpriteIcon } from '@/components/ui/SvgSprite';
import { CoinProps } from '@/types/coins';
import { TablePriceCell } from '@/components/(postTypes)/symbols/archive/table/TablePriceCell';
import ChangeValue from '@/components/(postTypes)/symbols/ChangeValue';
import { useCoinPriceSocket } from '@/hooks/useCoinPriceSocket';
import { sitePath } from '@/utils/links/safeHref';
import { isUsdQuoted, usdPriceOf } from '@/utils/coins/quote';

export interface MarketLiveWidgetGroup {
    title: string;
    type: 'gold' | 'currency';
    items: CoinProps[];
}

interface MarketLiveWidgetProps {
    groups: MarketLiveWidgetGroup[];
    currentSymbol?: string;
}

/**
 * Live toman prices of the key gold & fiat symbols (the current page's symbol is left out);
 * each item links to its own symbol page.
 * Mobile: one horizontally scrolling row of cards per group; sm: a grid; lg (sidebar): a compact list.
 */
/** 24h change that follows the socket (same shared connection as the price cell) */
const LiveChange: React.FC<{
    symbol: string;
    type: 'gold' | 'currency';
    initialChange: number;
    className: string;
}> = ({ symbol, type, initialChange, className }) => {
    const { liveChange24h } = useCoinPriceSocket(symbol, 0, 0, type);
    return <ChangeValue change={liveChange24h ?? initialChange} className={className} />;
};

const MarketLiveWidget: React.FC<MarketLiveWidgetProps> = ({ groups, currentSymbol }) => {
    const current = currentSymbol?.toLowerCase();
    const visibleGroups = groups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => (item.symbol || '').toLowerCase() !== current),
        }))
        .filter((group) => group.items.length > 0);
    if (visibleGroups.length === 0) return null;

    return (
        <div className='w-full min-w-0 flex flex-col gap-4'>
            {visibleGroups.map((group) => (
                <div key={group.type} className='w-full min-w-0'>
                    {/* Desktop sidebar: the title row doubles as the table header (unit and period) */}
                    <div className='mb-2 lg:mb-0 lg:pb-1.5 lg:border-b lg:border-gray-200 lg:dark:border-white/10 lg:grid lg:grid-cols-[minmax(0,1fr)_104px_52px] lg:items-end lg:gap-2 lg:px-2'>
                        <h3 className='text-xs font-bold text-zinc-500 dark:text-gray-400'>
                            {group.title}
                        </h3>
                        <span className='hidden lg:block text-end text-[10px] text-zinc-400 dark:text-gray-500'>
                            قیمت (تومان)
                        </span>
                        <span className='hidden lg:block text-end text-[10px] text-zinc-400 dark:text-gray-500'>
                            ۲۴ ساعت
                        </span>
                    </div>
                    <ul className='no-scrollbar flex gap-2 overflow-x-auto snap-x snap-mandatory px-4 pb-1 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-3 sm:overflow-visible lg:flex lg:flex-col lg:gap-0 lg:divide-y lg:divide-gray-100 lg:dark:divide-white/5'>
                        {group.items.map((item) => {
                            const symbol = (item.symbol || '').toLowerCase();
                            const label = item.name_fa || item.nameFa || item.name || symbol;
                            // The world gold ounce is quoted in dollars: dollars first, toman under it
                            const usdQuoted = isUsdQuoted(item);
                            return (
                                <li
                                    key={symbol}
                                    className='snap-start shrink-0 w-[150px] sm:w-auto'
                                >
                                    <Link
                                        href={sitePath('symbol', symbol) ?? '#'}
                                        prefetch={false}
                                        className='flex h-full flex-col gap-1.5 rounded-xl border p-2.5 transition-colors border-gray-100 bg-white hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 lg:grid lg:grid-cols-[minmax(0,1fr)_104px_52px] lg:items-center lg:gap-2 lg:rounded-lg lg:border-transparent lg:bg-transparent lg:px-2 lg:py-1.5 lg:dark:border-transparent lg:dark:bg-transparent'
                                    >
                                        <span className='flex min-w-0 items-center gap-1.5'>
                                            {item.iconUrl ? (
                                                <Image
                                                    src={item.iconUrl}
                                                    alt={label}
                                                    width={20}
                                                    height={20}
                                                    className='size-5 shrink-0 rounded-full object-cover'
                                                />
                                            ) : (
                                                <span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-400/15 dark:text-amber-300'>
                                                    <SpriteIcon name='coins' size={12} className='size-3' />
                                                </span>
                                            )}
                                            <span className='truncate text-xs font-medium text-zinc-900 dark:text-white'>
                                                {label}
                                            </span>
                                        </span>
                                        <span className='flex flex-col gap-1 lg:contents'>
                                            <span className='text-zinc-900 dark:text-white lg:justify-self-end'>
                                                <TablePriceCell
                                                    symbol={symbol}
                                                    initialPriceUsdt={usdPriceOf(item)}
                                                    initialPriceToman={Number(item.price) || 0}
                                                    hideUsdtPrice={!usdQuoted}
                                                    unitClassName='lg:hidden'
                                                    symbolType={group.type}
                                                />
                                            </span>
                                            <LiveChange
                                                symbol={symbol}
                                                type={group.type}
                                                initialChange={Number(item.change) || 0}
                                                className='text-[10px] self-start lg:self-auto lg:justify-self-end'
                                            />
                                        </span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
};

export default MarketLiveWidget;
